/**
 * Voice API Client - WebRTC wrapper for OpenAI Realtime API interactions
 * Handles authentication, rate limiting, error management, and connection health
 */

// Configuration constants
const CONNECTION_TIMEOUT = 30000; // 30 seconds
const RECONNECT_ATTEMPTS = 3;
const RECONNECT_DELAY = 2000; // 2 seconds
const RESPONSE_CREATE_DEBOUNCE_MS = 350; // avoid back-to-back creates

// Error types for better error handling
export const ERROR_TYPES = {
  AUTHENTICATION: 'AUTHENTICATION_ERROR',
  CONNECTION: 'CONNECTION_ERROR',
  API_LIMIT: 'API_LIMIT_ERROR',
  AUDIO: 'AUDIO_ERROR',
  TIMEOUT: 'TIMEOUT_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR'
};

// Session limits for security
const SESSION_LIMITS = {
  MAX_DURATION: parseInt(process.env.REACT_APP_MAX_CONVERSATION_DURATION) || 900000, // 15 minutes hard cap
  TIMEOUT: parseInt(process.env.REACT_APP_VOICE_SESSION_TIMEOUT) || 120000, // 2 minutes idle cap (cost control)
  MAX_RETRIES: 3
};

/**
 * WebRTC Voice API Client class
 */
class VoiceApiClient {
  constructor() {
    this.peerConnection = null;
    this.dataChannel = null;
    this.audioElement = null;
    this.isConnecting = false;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.sessionStartTime = null;
    this.lastActivityTime = null;
    this.sessionTimeout = null;
    this.eventHandlers = new Map();
    this.currentApiKey = null; // Store the current API key for reconnections
    this.currentSystemPrompt = null; // Store system prompt for reconnections
    this.assistantSpeaking = false; // exposed for status only
    this.responseInProgress = false;
    this.pendingResponse = null;
    this.lastResponseCreateAt = 0;
    this.localStream = null; // Store the microphone stream
    this.pendingSystemPrompt = null; // Store system prompt until session.created
    this.isPaused = false; // Track pause state for audio control
  }

  /**
   * Validate API key format
   */
  validateApiKey(apiKey) {
    if (!apiKey || typeof apiKey !== 'string') {
      throw new Error('OpenAI API key is required. Please configure your API key in settings.');
    }

    const trimmedKey = apiKey.trim();
    
    if (trimmedKey === 'your_openai_api_key_here' || trimmedKey === 'sk-') {
      throw new Error('Please configure your actual OpenAI API key in settings.');
    }

    if (!trimmedKey.startsWith('sk-')) {
      throw new Error('Invalid OpenAI API key format. API keys should start with "sk-".');
    }

    if (trimmedKey.length < 20) {
      throw new Error('OpenAI API key appears to be too short. Please verify your API key.');
    }

    return trimmedKey;
  }

  /**
   * Check if browser supports required features
   */
  checkBrowserSupport() {
    const errors = [];

    if (!window.RTCPeerConnection) {
      errors.push('WebRTC support is required');
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      errors.push('Microphone access (getUserMedia) is required');
    }

    if (!window.RTCPeerConnection) {
      errors.push('WebRTC support is required');
    }

    if (errors.length > 0) {
      throw new Error(`Browser compatibility issues: ${errors.join(', ')}`);
    }
  }

  /**
   * Set up session timeout monitoring
   */
  setupSessionMonitoring() {
    this.sessionStartTime = Date.now();
    this.lastActivityTime = Date.now();

    // Set up session timeout
    this.sessionTimeout = setTimeout(() => {
      this.emit('session-timeout', 'Session timed out due to inactivity');
      this.disconnect();
    }, SESSION_LIMITS.TIMEOUT);
  }

  /**
   * Update last activity time
   */
  updateActivity() {
    this.lastActivityTime = Date.now();
    
    // Reset timeout
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout);
      this.sessionTimeout = setTimeout(() => {
        this.emit('session-timeout', 'Session timed out due to inactivity');
        this.disconnect();
      }, SESSION_LIMITS.TIMEOUT);
    }
  }

  /**
   * Check session limits
   */
  checkSessionLimits() {
    if (!this.sessionStartTime) return true;

    const sessionDuration = Date.now() - this.sessionStartTime;
    
    if (sessionDuration > SESSION_LIMITS.MAX_DURATION) {
      this.emit('session-limit-exceeded', 'Maximum session duration exceeded');
      return false;
    }

    return true;
  }

  /**
   * Pause session timeout (for when user pauses the conversation)
   */
  pauseSessionTimeout() {
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout);
      this.sessionTimeout = null;
    }
  }

  /**
   * Resume session timeout (for when user resumes the conversation)
   */
  resumeSessionTimeout() {
    if (!this.sessionTimeout && this.sessionStartTime) {
      this.sessionTimeout = setTimeout(() => {
        this.emit('session-timeout', 'Session timed out due to inactivity');
        this.disconnect();
      }, SESSION_LIMITS.TIMEOUT);
    }
  }

  /**
   * Connect to OpenAI Realtime API via WebRTC
   */
  async connect(systemPrompt, userApiKey, mediaStream = null) {
    try {
      if (this.isConnecting || this.isConnected) {
        throw new Error('Connection already in progress or established');
      }

      this.isConnecting = true;

      // Validate API key and browser support
      const apiKey = this.validateApiKey(userApiKey);
      this.currentApiKey = apiKey; // Store for reconnections
      this.currentSystemPrompt = systemPrompt; // Store for reconnections
      this.checkBrowserSupport();

      // Setup session monitoring
      this.setupSessionMonitoring();

      // Create peer connection
      const pc = new RTCPeerConnection();
      this.peerConnection = pc;

      // Set up audio element for remote audio from the model
      this.audioElement = document.createElement('audio');
      this.audioElement.autoplay = true;
      pc.ontrack = (e) => {
        this.audioElement.srcObject = e.streams[0];
      };

      // Add local audio track for microphone input if provided
      if (mediaStream) {
        this.localStream = mediaStream;
        const audioTrack = mediaStream.getAudioTracks()[0];
        if (audioTrack) {
          pc.addTrack(audioTrack, mediaStream);
        }
      }

      // Set up data channel for sending and receiving events
      const dc = pc.createDataChannel('oai-events');
      this.dataChannel = dc;

      // Set up data channel event handlers
      dc.onopen = () => {
        this.isConnecting = false;
        this.isConnected = true;
        this.reconnectAttempts = 0;

        this.emit('connected');

        // Don't send session config immediately - wait for session.created event
        // Store system prompt for when we receive session.created
        this.pendingSystemPrompt = systemPrompt;
      };

      dc.onmessage = (event) => {
        this.updateActivity();
        this.handleMessage(event);
      };

      dc.onerror = (error) => {
        console.error('Data channel error:', error);
        this.handleError(ERROR_TYPES.CONNECTION, 'Data channel error');
      };

      dc.onclose = () => {
        this.isConnecting = false;
        this.isConnected = false;
        this.emit('disconnected', 'Data channel closed');
      };

      // Handle peer connection state changes
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'failed' && this.reconnectAttempts < RECONNECT_ATTEMPTS) {
          this.attemptReconnect(systemPrompt, apiKey, mediaStream);
        }
      };

      // Start the session using the Session Description Protocol (SDP)
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const model = process.env.REACT_APP_VOICE_MODEL || 'gpt-realtime-2.1-mini';
      const baseUrl = 'https://api.openai.com/v1/realtime/calls';

      // Set connection timeout
      const timeout = setTimeout(() => {
        if (!this.isConnected) {
          this.handleError(ERROR_TYPES.TIMEOUT, 'Connection timeout');
          this.cleanup();
        }
      }, CONNECTION_TIMEOUT);

      const sdpResponse = await fetch(`${baseUrl}?model=${model}`, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${apiKey}`,
          'Content-Type': 'application/sdp',
        },
      });

      if (!sdpResponse.ok) {
        clearTimeout(timeout);
        const errorText = await sdpResponse.text();
        if (sdpResponse.status === 401) {
          this.handleError(ERROR_TYPES.AUTHENTICATION, 'Authentication failed - check your API key');
        } else if (sdpResponse.status === 429) {
          this.handleError(ERROR_TYPES.API_LIMIT, 'API rate limit exceeded');
        } else {
          this.handleError(ERROR_TYPES.CONNECTION, `HTTP ${sdpResponse.status}: ${errorText}`);
        }
        throw new Error(`HTTP ${sdpResponse.status}: ${errorText}`);
      }

      const sdp = await sdpResponse.text();
      const answer = { type: 'answer', sdp };
      await pc.setRemoteDescription(answer);

      clearTimeout(timeout);

    } catch (error) {
      this.isConnecting = false;
      this.cleanup();
      this.handleError(ERROR_TYPES.UNKNOWN, error.message);
      throw error;
    }
  }

  /**
   * Send session configuration
   */
  sendSessionConfig(systemPrompt) {
    if (!this.isConnected || !this.dataChannel) return;

    // Encourage proactive questioning to keep flow moving
    const turnTakingNote = '\n\nTurn-taking: Always end your turn with one concise question to the student.';

    const config = {
      type: 'session.update',
      session: {
        type: "realtime",
        instructions: `${systemPrompt}${turnTakingNote}`,
        output_modalities: ["audio"],
        audio: {
          input: {
            format: {
              type: "audio/pcm",
              rate: 24000,
            },
            turn_detection: {
              type: "semantic_vad"
            }
          },
          output: {
            format: {
              type: "audio/pcm",
              rate: 24000,
            },
            voice: "echo",
          }
        },
      }
    };

    this.sendClientEvent(config);
  }

  /**
   * Handle incoming messages
   */
  handleMessage(event) {
    try {
      const message = JSON.parse(event.data);

      // If paused, ignore audio-related messages but still handle system messages
      if (this.isPaused && this.isAudioRelatedMessage(message.type)) {
        return;
      }

      this.emit('message', message);

      // Handle specific message types
      switch (message.type) {
        case 'error':
          this.handleApiError(message.error);
          return;
        case 'session.created':
          // Session is now ready to receive configuration
          if (this.pendingSystemPrompt) {
            this.sendSessionConfig(this.pendingSystemPrompt);
            this.pendingSystemPrompt = null;
          }
          this.emit('session-ready');
          return;
        default:
          break;
      }

      // Treat any response.* events generically to maintain accurate state
      if (typeof message.type === 'string' && message.type.startsWith('response.')) {
        const t = message.type;
        const isDone = t.endsWith('.done') || t === 'response.done' || t.endsWith('.completed') || t.endsWith('.cancelled');
        if (isDone) {
          this.assistantSpeaking = false;
          this.responseInProgress = false;
          if (this.pendingResponse && !this.isPaused) {
            this.pendingResponse = null;
            try { this.send({ type: 'response.create' }); this.lastResponseCreateAt = Date.now(); } catch (_) {}
          }
        } else if (t.startsWith('response.')) {
          // Only set responseInProgress for actual response events, not error events
          this.responseInProgress = true;
          if (t.includes('audio')) {
            this.assistantSpeaking = true;
          }
        }
      }
    } catch (error) {
      console.error('Failed to parse data channel message:', error);
      this.handleError(ERROR_TYPES.UNKNOWN, 'Failed to parse server response');
    }
  }

  /**
   * Check if a message type is audio-related and should be ignored when paused
   */
  isAudioRelatedMessage(messageType) {
    if (!messageType) return false;

    const audioRelatedTypes = [
      'conversation.item.input_audio_transcription',
      'response.audio_transcript',
      'response.audio',
      'input_audio_buffer'
    ];

    return audioRelatedTypes.some(type => messageType.includes(type));
  }

  /**
   * Handle API errors
   */
  handleApiError(error) {
    let errorType = ERROR_TYPES.UNKNOWN;
    let message = error.message || 'Unknown API error';

    if (error.code) {
      switch (error.code) {
        case 'invalid_api_key':
        case 'authentication_failed':
          errorType = ERROR_TYPES.AUTHENTICATION;
          break;
        case 'rate_limit_exceeded':
        case 'quota_exceeded':
          errorType = ERROR_TYPES.API_LIMIT;
          break;
        default:
          errorType = ERROR_TYPES.UNKNOWN;
      }
    }

    // Guard: if server complains about active response, treat as in-progress and queue
    if (typeof message === 'string' && message.toLowerCase().includes('active response in progress')) {
      this.responseInProgress = true;
      // Do not emit as hard error to UI; it is a flow-control signal
      return;
    }

    // Guard: if server complains about no active response for cancel, don't treat as error
    if (typeof message === 'string' && message.toLowerCase().includes('no active response found')) {
      this.responseInProgress = false;
      this.assistantSpeaking = false;
      // Do not emit as hard error to UI; it is a flow-control signal
      return;
    }

    this.handleError(errorType, message);
  }

  /**
   * Generic error handler
   */
  handleError(type, message) {
    console.error(`Voice API Error [${type}]:`, message);
    this.emit('error', { type, message });
  }

  /**
   * Attempt reconnection
   */
  attemptReconnect(systemPrompt, userApiKey, mediaStream) {
    if (this.reconnectAttempts >= RECONNECT_ATTEMPTS) {
      this.handleError(ERROR_TYPES.CONNECTION, 'Maximum reconnection attempts exceeded');
      return;
    }

    this.reconnectAttempts++;

    // Clean up current connection first
    this.cleanup();

    setTimeout(() => {
      if (!this.isConnected) {
        this.connect(
          systemPrompt || this.currentSystemPrompt,
          userApiKey || this.currentApiKey,
          mediaStream || this.localStream
        ).catch(error => {
          console.error('Reconnection failed:', error);
        });
      }
    }, RECONNECT_DELAY * this.reconnectAttempts);
  }

  /**
   * Send client event to the API via data channel
   */
  sendClientEvent(message) {
    if (!this.isConnected || !this.dataChannel) {
      throw new Error('Not connected to voice service');
    }

    if (!this.checkSessionLimits()) {
      this.disconnect();
      return;
    }

    this.updateActivity();

    // Add event ID if not present
    if (!message.event_id) {
      message.event_id = crypto.randomUUID();
    }

    this.dataChannel.send(JSON.stringify(message));
  }

  /**
   * Send data to the API (alias for backward compatibility)
   */
  send(data) {
    this.sendClientEvent(data);
  }

  /**
   * Send audio data (WebRTC handles audio automatically, so this is for manual audio sending)
   */
  sendAudio(audioData) {
    this.sendClientEvent({
      type: 'input_audio_buffer.append',
      audio: audioData
    });
  }

  /**
   * Cancel any in-progress assistant response (for barge-in)
   */
  cancelResponse() {
    try {
      // Only attempt to cancel if there's actually a response in progress
      if (!this.responseInProgress && !this.assistantSpeaking) {
        return;
      }

      this.send({ type: 'response.cancel' });
      
      // Update state immediately
      this.assistantSpeaking = false;
      // Keep responseInProgress true until server confirms done/cancelled
      
    } catch (err) {
      console.error('Failed to cancel response:', err);
    }
  }

  /**
   * Request assistant response with guard against concurrent responses
   */
  requestResponse(modalities = ['audio']) {
    if (!this.isConnected) return false;
    const now = Date.now();
    const tooSoon = now - (this.lastResponseCreateAt || 0) < RESPONSE_CREATE_DEBOUNCE_MS;
    if (this.responseInProgress || tooSoon) {
      // Queue a response; it will auto-send on next response.done
      this.pendingResponse = modalities;
      return false;
    }
    try {
      this.send({ type: 'response.create' });
      this.responseInProgress = true;
      this.lastResponseCreateAt = now;
      return true;
    } catch (e) {
      console.error('Failed to request response:', e);
      return false;
    }
  }

  /**
   * Pause audio playback and input
   */
  pauseAudio() {
    try {
      this.isPaused = true;

      // Pause audio playback immediately
      if (this.audioElement) {
        this.audioElement.pause();
      }

      // Mute microphone tracks to stop sending audio
      if (this.localStream) {
        this.localStream.getAudioTracks().forEach(track => {
          track.enabled = false;
        });
      }

      // Cancel any ongoing response
      if (this.responseInProgress || this.assistantSpeaking) {
        this.cancelResponse();
      }

    } catch (error) {
      console.error('Error pausing audio:', error);
    }
  }

  /**
   * Resume audio playback and input
   */
  resumeAudio() {
    try {
      this.isPaused = false;

      // Resume audio playback
      if (this.audioElement) {
        this.audioElement.play().catch(err => {
          // Audio might already be playing or no audio to play
          console.debug('Audio resume info:', err.message);
        });
      }

      // Unmute microphone tracks to resume sending audio
      if (this.localStream) {
        this.localStream.getAudioTracks().forEach(track => {
          track.enabled = true;
        });
      }

    } catch (error) {
      console.error('Error resuming audio:', error);
    }
  }

  /**
   * Clean up WebRTC resources
   */
  cleanup() {
    if (this.dataChannel) {
      this.dataChannel.close();
      this.dataChannel = null;
    }

    if (this.peerConnection) {
      // Stop all tracks
      this.peerConnection.getSenders().forEach((sender) => {
        if (sender.track) {
          sender.track.stop();
        }
      });

      this.peerConnection.close();
      this.peerConnection = null;
    }

    if (this.audioElement) {
      this.audioElement.srcObject = null;
      this.audioElement = null;
    }

    this.localStream = null;
    this.isConnecting = false;
    this.isConnected = false;
    this.assistantSpeaking = false;
    this.responseInProgress = false;
    this.pendingSystemPrompt = null;
    this.isPaused = false;
  }

  /**
   * Disconnect from the API
   */
  disconnect() {
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout);
      this.sessionTimeout = null;
    }

    this.cleanup();

    this.sessionStartTime = null;
    this.lastActivityTime = null;
    this.currentApiKey = null;
    this.currentSystemPrompt = null;
  }

  /**
   * Event emitter functionality
   */
  on(event, handler) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event).push(handler);
  }

  off(event, handler) {
    if (!this.eventHandlers.has(event)) return;
    const handlers = this.eventHandlers.get(event);
    const index = handlers.indexOf(handler);
    if (index > -1) {
      handlers.splice(index, 1);
    }
  }

  emit(event, data) {
    if (!this.eventHandlers.has(event)) return;
    this.eventHandlers.get(event).forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error(`Error in event handler for ${event}:`, error);
      }
    });
  }

  /**
   * Get connection status
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      isConnecting: this.isConnecting,
      reconnectAttempts: this.reconnectAttempts,
      sessionDuration: this.sessionStartTime ? Date.now() - this.sessionStartTime : 0,
      lastActivity: this.lastActivityTime,
      assistantSpeaking: this.assistantSpeaking,
      responseInProgress: this.responseInProgress
    };
  }
}

// Export singleton instance
export const voiceApiClient = new VoiceApiClient();
export default VoiceApiClient;
